import {Injectable, Injector, inject} from "@angular/core";
import {
    collection,
    component,
    EntityData,
    EntityModel,
    FedeltaImageData,
    field,
    identifier,
    ImageUtils,
    transientField
} from "@fedelta-pos/data-client";

export enum AppLoginIdentificationMethod {
    EMAIL = "email",
    MOBILE = "mobile",
    MEMBER_NO = "memberno",
    MEMBER_NO_PLUS_MOBILE = "membernoplusmobile"
}

export enum AppLoginAuthenticationMethod {
    // noinspection JSUnusedGlobalSymbols
    NONE = "none",
    PASSWORD = "password",
    DOB = "dob",
    CODE = "code"
}

export enum AppDealActionStyle {
    INLINE = "inline",
    CORNER = "corner",
    PEEL = "peel"
}

export enum AppPromoTextPosition {
    TOP = "top",
    CENTER = "center",
    BOTTOM = "bottom"
}

export enum AppHeaderPlacement {
    FULL_WIDTH = "FULL_WIDTH",
    CONTENT = "CONTENT"
}

export enum AppNavbarPlacement {
    PAGE_HEADER = "PAGE_HEADER",
    SIDEBAR = "SIDEBAR"
}

export enum TableLookupMethod {
    MANUAL = "Manual",
    QR_CODE = "QR Code"
}

export enum ButtonShape {
    SQUARE = "SQUARE",
    ROUNDED = "ROUNDED",
    OVAL = "OVAL"
}

export enum ButtonStyle {
    FLAT = "FLAT",
    OUTLINE = "OUTLINE",
    RAISED = "RAISED"
}

export class AppFeedbackData extends EntityData<AppFeedbackData> {
    @field categoryName: string;
}

export class ColorPairData extends EntityData<ColorPairData> {
    @field foregroundColor: string;
    @field backgroundColor: string;
}

export class AppThemePaletteEntryData extends EntityData<AppThemePaletteEntryData> {
    @field name: string;
    @component colorPair: ColorPairData;
}

export class MessageData extends EntityData<AppThemePaletteEntryData> {
    @field id: number;
}

export class AppCardThemeData extends EntityData<AppCardThemeData> {
    @field name: string;
    @field widthPx: number;
    @field heightPx: number;
    @field bodyPadding: string;
    @field backgroundColor: string;
    @field maxColumnSpec: string;
    @field headerTextColor: string;
    @field headerBackgroundColor: string;
    @field headerBackgroundOpacity: number;
    @field footerTextColor: string;
    @field footerBackgroundColor: string;
    @field footerBackgroundOpacity: number;
    @field placeholderBackgroundColor: string;
    @field placeholderBorderColor: string;
    @field placeholderOpacity: number;
    @field stampColor: string;
    @field stampOpacity: number;
    @field svgStampIcon: string;
    @field stampIconScale: number;
    @field stampIconRotateFactor: number;
}

export class AppCustomTextData extends EntityData<AppCustomTextData> {
    @identifier id: number;
    @field key: string;
    @field value: string;
}

export class AppThemeSettingValueData extends EntityData<AppThemeSettingValueData> {
    @identifier id: number;
    @field key: string;
    @field value: string;
}

export class AppSettingData extends EntityData<AppSettingData> {
    @identifier id: number;
    @field name: string;
    @field value: string;
}

export class AppData extends EntityData<AppData> {
    @identifier id: number;
    @field clientIpAddress: string;
    @field appName: string;
    @field shortAppName: string;
    @field clientId: string;
    @field appLiveMode: boolean = true;
    @field defaultMenuId: number;
    @field stripeTestPublicKey: string;
    @field googlePayMerchantName: string;
    @field googlePayMerchantId: string;
    @field integratedLoyalty: boolean = false;
    @field loginIdentificationMethod: AppLoginIdentificationMethod;
    @field loginAuthenticationMethod: AppLoginAuthenticationMethod;
    @field supportsAutomaticLogin: boolean = false;
    @field supportsRegistration: boolean;
    @field captureMemberDOB: boolean;
    @field supportsForgotPassword: boolean;
    @field supportsRedemption: boolean;
    @field memberNoIsNumeric: boolean = false;
    @field signupDelayMillis: number;
    @field defaultImage: string;
    @field dealActionStyle: AppDealActionStyle = AppDealActionStyle.PEEL;
    @field promoTextPosition: AppPromoTextPosition = AppPromoTextPosition.TOP;
    @field showDesktopMenuPromos: boolean;
    @field showMobileMenuPromos: boolean;
    @field storeRange: number;
    @field storeSearchRange: number;
    @field storeLimit: number;
    @field showStoreImagesOnStoreSelection: boolean = false;
    @field showStoreImagesOnMobileStoreSelection: boolean = false;
    @field deliveryStoreRange: number;
    @field deliveryStoreLimit: number;
    @field deliveryDistanceByRoad: boolean;
    @field headerPlacement: AppHeaderPlacement = AppHeaderPlacement.CONTENT;
    @field navbarPlacement: AppNavbarPlacement = AppNavbarPlacement.SIDEBAR;
    @field showDesktopHeaderImage: boolean = false;
    @field showDesktopHeaderLogo: boolean = true;
    @field showDesktopHeaderVerticalLogo: boolean = false;
    @field showMobileHeaderVerticalLogo: boolean = false;
    @field tableLookupMethod: TableLookupMethod = TableLookupMethod.MANUAL;
    @field onlineManualTableLookup: boolean = true;
    @field tableOrderingName: string;
    @field showPhoneNumberForPickup: boolean = false;
    @field showPhoneNumberForDelivery: boolean = true;
    @field showPhoneNumberForTable: boolean = false;
    @field showPhoneNumberForWaiter: boolean = false;
    @field requirePhoneNumberForPickup: boolean = false;
    @field requirePhoneNumberForDelivery: boolean = true;
    @field requirePhoneNumberForTable: boolean = false;
    @field requirePhoneNumberForWaiter: boolean = false;
    @field showEmailForPickup: boolean = true;
    @field showEmailForDelivery: boolean = true;
    @field showEmailForTable: boolean = false;
    @field showEmailForWaiter: boolean = false;
    @field requireEmailForPickup: boolean = false;
    @field requireEmailForDelivery: boolean = false;
    @field requireEmailForTable: boolean = false;
    @field requireEmailForWaiter: boolean = false;
    @field showNameForPickup: boolean = true;
    @field showNameForDelivery: boolean = true;
    @field showNameForTable: boolean = false;
    @field showNameForWaiter: boolean = false;
    @field requireFirstNameForPickup: boolean = true;
    @field requireFirstNameForDelivery: boolean = true;
    @field requireFirstNameForTable: boolean = false;
    @field requireFirstNameForWaiter: boolean = false;
    @field requireWaiterName: boolean = false;
    @field showStoreAddrOnStoreSelection: boolean = true;
    @field showStoreDescOnStoreSelection: boolean = false;
    @field sendOrderReceivedEmail: boolean = false;
    @field sendOrderReceivedSms: boolean = false;
    @component orderReceivedEmail: MessageData;
    @field sendOrderReadyEmail: boolean = false;
    @field sendOrderReadySms: boolean = false;
    @field sendTblOrderReadyForBistroOnly: boolean = true;
    @component orderReadyEmail: MessageData;
    @field expectedCountryCode: string = "61";
    @field desktopPillarboxing: boolean;
    @field showSelectedExtrasFirst: boolean;
    @field showTotalIncludedExtras: boolean;
    @field collapseExtras: boolean;
    @field compactExtrasThreshold: number;
    @field pointsName: string;
    @field memberNumberName: string;
    @field dietaryStatement: string;
    @field maxDaysOrderLater: number;
    @field selectionTimeInterval: number;
    @field showAsapSelectionTime: boolean;
    @field showTimeGrid: boolean;
    @field horizontalLogoImage: string;
    @field horizontalLogoHeightPx: number;
    @field verticalLogoImage: string;
    @field verticalLogoWidthPx: number;
    @component orderingImage: FedeltaImageData;
    @field orderingImageOpacity: number;
    @component loginImage: FedeltaImageData;
    @field loginImageOpacity: number;
    @field loginText1: string;
    @field loginText2: string;
    @collection(AppFeedbackData) feedbackCategories: AppFeedbackData[];
    @field allowMemberLogin: boolean;
    @field showNotes: boolean = true;
    @field promoPopupLimit: number;
    @field pairingLimit: number;
    @field dealLimit: number;
    @collection(AppThemePaletteEntryData) palette: AppThemePaletteEntryData[];
    @field firstTierButtonShape: ButtonShape = ButtonShape.ROUNDED;
    @field secondTierButtonShape: ButtonShape = ButtonShape.OVAL;
    @field firstTierHighProminenceButtonStyle: ButtonStyle = ButtonStyle.FLAT;
    @field secondTierHighProminenceButtonStyle: ButtonStyle = ButtonStyle.FLAT;
    @field firstTierLowProminenceButtonStyle: ButtonStyle = ButtonStyle.OUTLINE;
    @field secondTierLowProminenceButtonStyle: ButtonStyle = ButtonStyle.OUTLINE;
    @field integratedDelivery: boolean;
    @field startHourOfDay: number;
    @collection(AppCardThemeData) cardThemes: AppCardThemeData[];
    @collection(AppCustomTextData) customTexts: AppCustomTextData[];
    @collection(AppThemeSettingValueData) themeSettings: AppThemeSettingValueData[];
    @collection(AppSettingData) appSettings: AppSettingData[];
    @field promoteMemberLogin: boolean;
    @field promoteMemberLoginText1: string;
    @field promoteMemberLoginText2: string;
    @component promoteMemberLoginImage: FedeltaImageData;
    @field promoteMemberLoginImageAutoCrop: boolean;
    @field showMarketingOptIn: boolean = false;
    @field defaultMarketingOptIn: boolean = true;
    @field collectMarketingEmail: boolean = true;
    @field collectMarketingMobilePhone: boolean = false;
    @field collectMarketingPostCode: boolean = false;
    @transientField horizontalLogoImageSvg: string;
    @transientField verticalLogoImageSvg: string;
}

@Injectable()
export class AppEntityModel extends EntityModel<AppData> {
    constructor() {
        console.log("--- AppEntityModel");
        super(inject(Injector), AppData);
    }

    protected override onAfterLoadEntity(appData: AppData): void {
        appData.horizontalLogoImageSvg = ImageUtils.base64ToSvg(appData.horizontalLogoImage);
        appData.verticalLogoImageSvg = ImageUtils.base64ToSvg(appData.verticalLogoImage);
    }
}
